"use client"

import { useState, useEffect } from "react"
import { apiClient } from "@/lib/api-client"
import { RefreshCw } from "lucide-react"

interface PriceDisplayProps {
  usdAmount: number
  onPriceUpdate?: (price: number) => void
}

export function PriceDisplay({ usdAmount, onPriceUpdate }: PriceDisplayProps) {
  const [irmaPrice, setIrmaPrice] = useState<number | null>(null)
  const [irmaAmount, setIrmaAmount] = useState<number | null>(null)
  const [credits, setCredits] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>("")

  const fetchPrice = async () => {
    setLoading(true); setError("")
    try {
      const priceData = await apiClient.getIrmaPrice()
      setIrmaPrice(priceData.price)
      onPriceUpdate?.(priceData.price)
      if (usdAmount > 0) {
        const calculation = await apiClient.calculateIrmaFromUsd(usdAmount)
        setIrmaAmount(calculation.irmaAmountWithBonus)
        setCredits(calculation.creditsWithBonus)
      } else {
        setIrmaAmount(null); setCredits(null)
      }
    } catch (err: any) {
      setError(err.message || "Failed to fetch price")
      console.error("Error fetching price:", err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPrice()
    const interval = setInterval(fetchPrice, 30000)
    return () => clearInterval(interval)
  }, [usdAmount])

  if (error && !irmaPrice) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-sm text-red-400">
        <p>{error}</p>
        <button onClick={fetchPrice} className="mt-2 text-xs text-[#25e4e8] hover:underline">Retry</button>
      </div>
    )
  }

  return (
    <div className="bg-[#1C1F26] rounded-2xl p-4 border border-transparent">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-white">Pricing Details</span>
        <button type="button" onClick={fetchPrice} disabled={loading} className="text-[#8B949E] hover:text-white transition-colors">
          <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-sm text-[#8B949E]">Service Price:</span>
          <span className="font-semibold text-white">${usdAmount.toFixed(2)}</span>
        </div>

        {irmaPrice && (
          <>
            <div className="flex justify-between items-center">
              <span className="text-sm text-[#8B949E]">IRMA Price:</span>
              <span className="font-semibold text-white">${irmaPrice.toFixed(6)}</span>
            </div>

            {irmaAmount && (
              <>
                <div className="flex justify-between items-center pt-2 border-t border-white/5">
                  <span className="text-sm text-[#8B949E]">IRMA Required:</span>
                  <span className="font-semibold text-[#25e4e8]">{irmaAmount.toFixed(4)} IRMA</span>
                </div>

                <div className="bg-[#25e4e8]/10 border border-[#25e4e8]/20 rounded-xl p-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-[#25e4e8]">🎉 10% Bonus Applied!</span>
                    <span className="text-xs font-semibold text-[#25e4e8]">Pay 90% of calculated amount</span>
                  </div>
                </div>

                {credits && (
                  <div className="flex justify-between items-center pt-2 border-t border-white/5">
                    <span className="text-sm text-[#8B949E]">Credits Earned:</span>
                    <span className="font-semibold text-[#25e4e8]">{credits.toLocaleString()} credits</span>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {loading && !irmaPrice && (
          <p className="text-sm text-[#8B949E]">Loading price...</p>
        )}
      </div>
    </div>
  )
}
